import { useState } from 'react';
import Cookies from 'js-cookie';
import { useTranslation } from 'next-i18next';
import { UseRefinementListProps, useRefinementList } from 'react-instantsearch';
import { RefinementListItem } from 'instantsearch.js/es/connectors/refinement-list/connectRefinementList';

import { extractGroupFromLabel } from '@components/algolia/SearchResults/Sidebar/ClothingSizeRefinement';
import { childSizes } from '@components/algolia/SearchResults/Sidebar/ClothingSizeRefinement/clothingSizeConstants';
import useClothingSizeSorter from '@components/algolia/SearchResults/Sidebar/ClothingSizeRefinement/hooks/useClothingSizeSorter';
import useSizeGroups from '@components/algolia/SearchResults/Sidebar/ClothingSizeRefinement/hooks/useSizeGroups';
import { AlgoliaFacets } from '@utils/algolia/knownFacetNames';

import SelectionCell from '_components/UI/SelectionCell';

import styles from './ClothingSizeRefinement.module.scss';

export type ClothingSizeRefinementProps = UseRefinementListProps & {
  locale?: string;
};

type GroupedItem = RefinementListItem & { group: string };

const shouldShowGroupHeading = (attribute: string, group: string) =>
  attribute === AlgoliaFacets.ClothingSizeKids && childSizes.includes(group);

const ClothingSizeRefinement = (props: ClothingSizeRefinementProps) => {
  const { locale, ...refinementListProps } = props;

  const [debugAlgoliaAttribute] = useState(() =>
    Cookies.get('debugAlgoliaClothingSizeAttribute')
  );
  const customSort = useClothingSizeSorter(
    props.attribute === debugAlgoliaAttribute,
    locale
  );

  const { items, refine, canToggleShowMore, isShowingMore, toggleShowMore } =
    useRefinementList({
      ...refinementListProps,
      limit: 6,
      showMore: true,
      showMoreLimit: 1000,
      operator: 'or',
      transformItems: extractGroupFromLabel,
      sortBy: customSort,
    });

  const { t } = useTranslation(['searchResults']);

  const groupedItems = items as GroupedItem[];
  const sizeGroups = useSizeGroups(groupedItems);

  if (groupedItems.length === 0) return null;

  return (
    <div className={styles.root}>
      {sizeGroups.map((group) => (
        <div className={styles.group} key={group}>
          {shouldShowGroupHeading(props.attribute, group) && (
            <p className={styles.groupTitle}>{group}</p>
          )}
          <div className={styles.chips}>
            {groupedItems
              .filter((item) => item.group === group)
              .map((item) => (
                <SelectionCell
                  key={`${item.value}`}
                  selected={item.isRefined}
                  disabled={item.count === 0}
                  onClick={() => {
                    refine(item.value);
                  }}
                >
                  {item.label}
                </SelectionCell>
              ))}
          </div>
        </div>
      ))}
      {canToggleShowMore && (
        <button
          type="button"
          onClick={() => toggleShowMore()}
          className={styles.showMore}
        >
          {isShowingMore ? t('search.showless') : t('search.showmore')}
        </button>
      )}
    </div>
  );
};

export default ClothingSizeRefinement;
